<script setup>
// Ficha do sócio no Painel do Torcedor (R1.21), alimentada pela API da FENG.
//
// O princípio aqui é não despejar os dezesseis campos que a FENG devolve. O agente tem poucos
// segundos antes de responder, então a tela mostra, nesta ordem: quem é (sócio e plano), até
// quando, o que EXIGE ação, e só depois o que é bom saber.
//
// Consulta sob demanda, ao abrir a conversa. Não em toda mensagem: é dado pessoal de terceiro
// e cada acesso precisa de motivo.
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFichaSocio } from 'dashboard/composables/useFichaSocio';

const props = defineProps({
  conversationId: { type: [String, Number], default: null },
  // T-109: o CPF entra aqui vindo do cadastro do contato. Sem ele a ficha não teria como saber
  // que o agente acabou de digitar o documento, e o torcedor seguiria "sem CPF" até alguém
  // trocar de conversa e voltar.
  cpf: { type: String, default: '' },
});

const { t } = useI18n();
// O estado é compartilhado com o selo que aparece embaixo do nome do contato: os dois mostram a
// mesma consulta, e sem compartilhar a tela pediria duas vezes o que precisa uma.
const { garantir, dadosDe } = useFichaSocio();
const carregando = ref(false);

const dados = computed(() => dadosDe(props.conversationId, props.cpf));

const buscar = async () => {
  if (!props.conversationId) return;
  carregando.value = true;
  await garantir(props.conversationId, props.cpf);
  carregando.value = false;
};

// Observa a conversa E o CPF: o agente digita o documento na ficha do contato e a consulta
// roda na hora, sem precisar recarregar nada. O contato vem do store, então basta ele salvar.
watch(() => [props.conversationId, props.cpf].join('|'), buscar, {
  immediate: true,
});

// Sem integração configurada não existe ficha para mostrar: melhor não ocupar espaço com um
// aviso que o agente não pode resolver.
const oculto = computed(
  () => !carregando.value && dados.value?.disponivel === false && dados.value?.motivo === 'nao_configurado'
);
const semCpf = computed(() => dados.value?.motivo === 'sem_cpf');
const indisponivel = computed(
  () => dados.value?.disponivel === false && !semCpf.value && !oculto.value
);
// Pedido da reunião de 20/08: CPF fora da base deixa de virar aviso técnico e passa a ser
// exibido como NÃO SÓCIO, com o mesmo selo. A Esthephany apontou que "CPF não encontrado" ao
// lado do título confundia, e o Douglas pediu que a condição de não sócio ficasse EVIDENTE,
// justamente para o agente poder oferecer plano.
//
// A tela passa a concordar com o relatório: o retrato do ticket (T-101) já registra CPF fora
// da base como não sócio desde o início. Antes, o painel dizia uma coisa e o indicador outra.
const fichaAgora = computed(() => {
  const resposta = dados.value;
  if (!resposta || resposta.disponivel === false) return null;
  if (resposta.encontrado === false) return { socio: false };
  return resposta.encontrado === true ? resposta : null;
});

// T-230: o painel é o retrato do TICKET, não a situação de agora.
//
// Quem abre um atendimento de setembro em dezembro precisa ver o plano que valia em setembro,
// senão o histórico do caso não fecha: a conversa fala de um plano e o painel mostra outro.
// O congelado vem do servidor, gravado na primeira consulta daquela conversa; enquanto ele não
// existe (ticket que nunca foi consultado) a tela mostra o que a FENG responder na hora.
const fichaCongelada = computed(() => dados.value?.retrato_feng || null);
const ficha = computed(() => fichaCongelada.value || fichaAgora.value);

const dataFim = computed(() => {
  if (!ficha.value?.fim) return '';
  const [a, m, d] = ficha.value.fim.split('-');
  return `${d}/${m}/${a}`;
});

// "faltam 12 dias" age mais do que "01/12/2026". A data continua ali para quem precisa dela.
//
// A contagem é recalculada a partir da DATA congelada, e não copiada do congelado. A data de fim
// do contrato é um fato e congela; "faltam 12 dias" é uma frase relativa a hoje, e repetir a de
// setembro em dezembro seria dizer uma coisa falsa com cara de fato.
const diasParaVencer = computed(() => {
  const f = ficha.value;
  if (!f) return null;
  if (!f.fim) return f.dias_para_vencer ?? null;
  const [a, m, d] = f.fim.split('-').map(Number);
  const fim = Date.UTC(a, m - 1, d);
  const agora = new Date();
  const hoje = Date.UTC(
    agora.getFullYear(),
    agora.getMonth(),
    agora.getDate()
  );
  return Math.round((fim - hoje) / 86400000);
});

const prazo = computed(() => {
  const dias = diasParaVencer.value;
  if (dias === null || dias === undefined) return '';
  if (dias < 0) return t('FANDATA_SIDEBAR.FICHA_SOCIO.VENCIDO');
  if (dias === 0) return t('FANDATA_SIDEBAR.FICHA_SOCIO.VENCE_HOJE');
  if (dias <= 60) return t('FANDATA_SIDEBAR.FICHA_SOCIO.FALTAM_DIAS', { dias });
  return t('FANDATA_SIDEBAR.FICHA_SOCIO.FALTAM_MESES', { meses: Math.round(dias / 30) });
});

// T-102, opção C (escolhida pelo Douglas em 21/08), agora do lado oposto por causa do T-230.
//
// A regra continua a mesma: a segunda linha aparece SOMENTE quando houve mudança. Mostrar as
// duas sempre faria o agente ignorar as duas, como já aconteceu com o selo de sócio duplicado.
// O que virou de lado foi QUEM é o corpo e quem é a nota: o corpo passou a ser o retrato da
// abertura, e a nota passou a dizer como está HOJE. É a mesma informação, na ordem que o painel
// agora promete.
const mudouDesdeAbertura = computed(() => {
  const antes = fichaCongelada.value;
  const agora = fichaAgora.value;
  if (!antes || !agora) return false;
  if (Boolean(antes.socio) !== Boolean(agora.socio)) return true;
  // Mesmo vínculo, plano diferente: é o "vi que você fez um upgrade" que a Esthephany descreveu.
  return Boolean(antes.plano && agora.plano && antes.plano !== agora.plano);
});

const textoHoje = computed(() => {
  const agora = fichaAgora.value;
  if (!agora) return '';
  if (!agora.socio) return t('FANDATA_SIDEBAR.FICHA_SOCIO.HOJE_NAO_SOCIO');
  if (agora.plano && agora.plano !== fichaCongelada.value?.plano) {
    return t('FANDATA_SIDEBAR.FICHA_SOCIO.HOJE_PLANO', { plano: agora.plano });
  }
  return t('FANDATA_SIDEBAR.FICHA_SOCIO.HOJE_SOCIO');
});

// A data do retrato só aparece quando ele NÃO é de hoje. Num ticket aberto agora ela seria
// ruído: o retrato e o presente são a mesma coisa, e carimbar a data faria o agente procurar
// uma diferença que não existe.
const dataDoRetrato = computed(() => {
  const em = fichaCongelada.value?.em;
  if (!em) return '';
  const quando = new Date(em);
  if (Number.isNaN(quando.getTime())) return '';
  const hoje = new Date();
  if (quando.toDateString() === hoje.toDateString()) return '';
  const dd = String(quando.getDate()).padStart(2, '0');
  const mm = String(quando.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${quando.getFullYear()}`;
});

const selos = computed(() => {
  const f = ficha.value;
  if (!f) return [];
  const lista = [];
  if (f.ingressos) lista.push(t('FANDATA_SIDEBAR.FICHA_SOCIO.INGRESSOS', { n: f.ingressos }));
  if (f.pode_upgrade) lista.push(t('FANDATA_SIDEBAR.FICHA_SOCIO.UPGRADE'));
  if (f.renovacao_disponivel) lista.push(t('FANDATA_SIDEBAR.FICHA_SOCIO.RENOVACAO'));
  if (f.cartao_salvo) lista.push(t('FANDATA_SIDEBAR.FICHA_SOCIO.CARTAO'));
  return lista;
});
</script>

<template>
  <div v-if="!oculto" class="flex flex-col gap-1.5 pt-1 border-t border-n-weak">
    <span class="flex items-baseline gap-1.5 min-w-0">
      <span class="text-xs font-medium text-n-slate-11">
        {{ t('FANDATA_SIDEBAR.FICHA_SOCIO.TITULO') }}
      </span>
      <span v-if="dataDoRetrato" class="text-xs truncate text-n-slate-10">
        {{ t('FANDATA_SIDEBAR.FICHA_SOCIO.RETRATO_DE', { data: dataDoRetrato }) }}
      </span>
    </span>

    <span v-if="carregando" class="text-xs text-n-slate-10">
      {{ t('FANDATA_SIDEBAR.FICHA_SOCIO.CARREGANDO') }}
    </span>

    <!-- Sem CPF o agente PODE agir: é só pedir na conversa. Por isso aparece, ao contrário
         dos casos que só nós resolvemos. -->
    <span v-else-if="semCpf" class="text-xs text-n-slate-10">
      {{ t('FANDATA_SIDEBAR.FICHA_SOCIO.SEM_CPF') }}
    </span>

    <span v-else-if="indisponivel" class="text-xs text-n-slate-10">
      {{ t('FANDATA_SIDEBAR.FICHA_SOCIO.INDISPONIVEL') }}
    </span>

    <template v-else-if="ficha">
      <!-- Estrela dourada = sócio, estrela preta = não sócio. O ouro é a marca do Camisa 7 e
           o preto e branco é a do clube, então o par se explica sozinho, sem legenda.
           Tom fixo e não token do tema: dourado aqui é identidade, não estado de interface, e
           acompanharia a paleta se viesse de token. No tema escuro o preto puro sumiria, então
           lá a estrela vira cinza claro: continua sendo "a estrela sem ouro". -->
      <div class="flex flex-wrap items-center gap-1.5">
        <span
          class="inline-flex items-center gap-1 px-2 py-1 text-xs font-bold tracking-wide border rounded-md"
          :class="
            ficha.socio
              ? 'bg-[#d4af37]/15 border-[#d4af37]/50 text-[#7a5f13] dark:text-[#e8cf7a]'
              : 'bg-black/[0.04] border-black/15 text-[#3f3f46] dark:bg-white/[0.06] dark:border-white/15 dark:text-[#d4d4d8]'
          "
        >
          <span
            class="leading-none"
            :class="
              ficha.socio
                ? 'text-[#d4af37]'
                : 'text-[#18181b] dark:text-[#a1a1aa]'
            "
          >
            ★
          </span>
          {{ ficha.socio ? t('FANDATA_SIDEBAR.FICHA_SOCIO.E_SOCIO') : t('FANDATA_SIDEBAR.FICHA_SOCIO.NAO_SOCIO') }}
        </span>
        <span
          v-if="ficha.plano"
          class="px-1.5 py-0.5 text-xs font-semibold tracking-wide uppercase rounded"
          :class="
            ficha.socio
              ? 'bg-[#d4af37]/10 text-[#7a5f13] dark:text-[#e8cf7a]'
              : 'bg-n-alpha-2 text-n-slate-11'
          "
        >
          {{ ficha.plano }}
        </span>
      </div>

      <span v-if="dataFim" class="text-xs text-n-slate-11">
        {{ ficha.socio ? t('FANDATA_SIDEBAR.FICHA_SOCIO.ATE') : t('FANDATA_SIDEBAR.FICHA_SOCIO.TERMINOU_EM') }}
        {{ dataFim }}<template v-if="prazo && ficha.socio"> · {{ prazo }}</template>


      </span>

      <span
        v-if="mudouDesdeAbertura"
        class="px-2 py-1 text-xs rounded bg-n-alpha-2 text-n-slate-11"
      >
        ↪ {{ textoHoje }}
      </span>

      <!-- O alerta que justifica a ficha: perto de vencer E sem renovação automática. Nenhum
           dos dois campos sozinho pede ação; juntos, é a hora da retenção. -->
      <span
        v-if="ficha.risco_perda"
        class="px-2 py-1 text-xs font-medium rounded bg-n-ruby-3 text-n-ruby-11"
      >
        {{ t('FANDATA_SIDEBAR.FICHA_SOCIO.RISCO_PERDA') }}
      </span>
      <span
        v-else-if="ficha.vencendo"
        class="px-2 py-1 text-xs font-medium rounded bg-n-amber-3 text-n-amber-11"
      >
        {{ t('FANDATA_SIDEBAR.FICHA_SOCIO.VENCENDO') }}
      </span>

      <div v-if="selos.length" class="flex flex-wrap gap-1">
        <span
          v-for="s in selos"
          :key="s"
          class="px-1.5 py-0.5 text-[11px] font-medium rounded border border-n-weak text-n-slate-11"
        >
          {{ s }}
        </span>
      </div>

      <!-- Divergência de cadastro costuma SER o problema do atendimento ("não recebi o
           e-mail"). Mostramos QUAIS campos divergem, nunca os valores: o agente confirma com o
           torcedor, que é o certo, em vez de a tela expor o contato registrado. -->
      <span
        v-if="ficha.divergencias && ficha.divergencias.length"
        class="px-2 py-1 text-xs rounded bg-n-amber-3 text-n-amber-11"
      >
        {{ t('FANDATA_SIDEBAR.FICHA_SOCIO.DIVERGENCIA') }}
        {{ ficha.divergencias.map(d => t(`FANDATA_SIDEBAR.FICHA_SOCIO.CAMPO_${d.toUpperCase()}`)).join(' · ') }}
      </span>
    </template>
  </div>
</template>
